import { Injectable } from "@angular/core";
import { MeshBasicMaterial, Color, Mesh } from "three";
import { ColorHEX } from "~/src/utils/action";
import { CanvasConfig } from "~/src/utils/canvas-configuration";
import { Object3DUserData, OriginalColors } from "~/src/utils/shape";
import { FaceChoice } from "../../shared/models/shape-selector.model";
import { ComponentInteractionService } from "../component-interaction/component-interaction.service";
import { ColorCodes } from "~/src/utils/color-constants";

@Injectable({
    providedIn: "root",
})
export class MeshVisualService {
    constructor(private componentInteractionService: ComponentInteractionService) {}

    public applyColorToMesh(mesh: Mesh, selectedColor: string, selectedOption: string, materialIndex: number): void {
        const originalColors: OriginalColors = mesh.userData[Object3DUserData.originalColors];

        if (!Array.isArray(mesh.material)) return;

        if (selectedOption === FaceChoice.singleFace) {
            originalColors[materialIndex] = selectedColor.toUpperCase();
        } else if (selectedOption === FaceChoice.fullface) {
            originalColors.forEach((_, index) => (originalColors[index] = selectedColor.toUpperCase()));
        }
    }

    public storeOriginalColors(mesh: Mesh): void {
        if (mesh.userData[Object3DUserData.originalColors]) return;

        const originalColors: string[] = [];
        if (Array.isArray(mesh.material)) {
            mesh.material.forEach((material) => {
                if (material instanceof MeshBasicMaterial) {
                    originalColors.push("#".concat(material.color.getHexString().toUpperCase()));
                }
            });
        }
        mesh.userData[Object3DUserData.originalColors] = originalColors;
    }

    public revertToOriginalColors(mesh: Mesh): void {
        if (!Array.isArray(mesh.material)) return;
        const originalColors = mesh.userData[Object3DUserData.originalColors];
        if (originalColors && Array.isArray(originalColors)) {
            (mesh.material as MeshBasicMaterial[]).forEach((material, index) => {
                material.color.set(originalColors[index]);
                material.transparent = false;
                material.opacity = 1;
            });
        }
    }

    public setSelectionHighlight(mesh: Mesh): void {
        const invalidShapes = this.componentInteractionService.getInvalidMeshes;
        const validShapes = this.componentInteractionService.getValidMeshes;
        const isValidOrInvalid = invalidShapes.includes(mesh) || validShapes.includes(mesh);

        (mesh.material as MeshBasicMaterial[]).forEach((material) => {
            material.color.copy(new Color(ColorHEX.defaultColour));
            material.transparent = isValidOrInvalid;
            material.opacity = isValidOrInvalid ? CanvasConfig.transparentOpacity : 1;
        });
    }

    public removeSelectionHighlight(mesh: Mesh, isFromColorChange: boolean = false): void {
        if (!isFromColorChange) {
            const invalidShapes = this.componentInteractionService.getInvalidMeshes;
            const validShapes = this.componentInteractionService.getValidMeshes;

            if (invalidShapes.includes(mesh)) {
                this.markMeshAsInvalid(mesh);
                return;
            }

            if (validShapes.includes(mesh)) {
                this.markMeshAsValid(mesh);
                return;
            }
        }

        this.revertToOriginalColors(mesh);
    }

    public markMeshAsValid(mesh: Mesh): void {
        (mesh.material as MeshBasicMaterial[]).forEach((material) => {
            material.color.set(ColorCodes.transparentBlue);
            material.transparent = true;
            material.opacity = CanvasConfig.transparentOpacity;
        });
    }

    public markMeshAsInvalid(mesh: Mesh): void {
        (mesh.material as MeshBasicMaterial[]).forEach((material) => {
            material.color.set(ColorCodes.red);
            material.transparent = true;
            material.opacity = CanvasConfig.transparentOpacity;
        });
    }

    public setCollisionHighlight(mesh: Mesh): void {
        (mesh.material as MeshBasicMaterial[]).forEach((material) => {
            material.color.set(ColorCodes.red);
            material.transparent = true;
            material.opacity = CanvasConfig.transparentOpacity;
        });
    }

    public removeCollisionHighlight(mesh: Mesh, isMeshSelected: boolean = true): void {
        if (isMeshSelected) {
            this.setSelectionHighlight(mesh);
        } else {
            this.removeSelectionHighlight(mesh);
        }
    }
}
